import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { OperationInsights } from '@/components/ahu/operation-insights';
import { TemperatureCard } from '@/components/ahu/temperature-card';
import { ConsumptionCard } from '@/components/ahu/consumption-card';
import { AHUData, AHUTable } from '@/components/ahu/ahu-table';
import { supabase } from '@/api/supabase';
import { useDevices } from '@/api/hooks/useDevices';
import { ZoneContext } from '@/auth/zoneProvider';
import { Device, DevicesResponseData } from '@/api/devices/types';
import { apiMapper } from '@/utils/apiMapper';
import { useZones } from '@/api/hooks/useZones';
import { useAutopilotHook } from '@/api/hooks/useAutopilot';
import { Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useEnumMappings } from '@/contexts/enum-mappings-context';
import { Mode, OpMode, AutoPilotStatus } from '@/types';
import { Status } from '@/components/ahu/status-badge';
import ErrorBoundary from '@/components/Common/error-boundary';
import { Button } from '@/components/ui/button';

interface OperationalData {
  averageWorking?: number;
  avgToday?: number;
  units: Array<{
    status?: 'on' | 'off' | 'alarm';
    type?: 'cool' | 'fan' | 'dry';
    count: number;
  }>;
}

interface TempData {
  sensorIndoor: {
    temperature?: number;
    humidity?: number;
    avgToday: {
      temperature?: number;
      humidity?: number;
    };
  };
  ahuTemp: {
    supply?: number;
    return?: number;
    avgToday: {
      supply?: number;
      return?: number;
    };
  };
}

interface AutopilotData {
  id?: string;
  user?: string;
  savedPower?: number;
  consumption?: number;
  status?: string;
  lastUpdated?: string;
  currentGear?: {
    id: number;
    gear: string;
    config: {
      pmv_offset: number;
      description: string;
    };
  };
}

// Helper function to normalize status values from the API
const normalizeStatus = (status: string | undefined): Status | undefined => {
  if (!status) return undefined; // Return undefined for truly undefined/missing status

  // Convert to lowercase for case-insensitive comparison
  const statusLower = status.toLowerCase();

  // Handle various forms of 'on' status
  if (statusLower === 'on' || statusLower === 'true' || status === '1') return 'on';

  // Handle various forms of 'off' status
  if (statusLower === 'off' || statusLower === 'false' || status === '0') return 'off';

  // If we get here, just return the status as is
  return status as Status;
};

const mapDeviceToTableData = (device: Device): AHUData => {
  // Check if latest_data exists and has content
  const hasLatestData = device?.latest_data && Object.keys(device.latest_data).length > 0;

  return {
    id: device.device_id || '',
    autopilotId: device.autopilot?.id || '',
    deviceName: device.name || 'Unknown Device',
    // No default status - leave undefined if no data
    status: hasLatestData ? normalizeStatus(device?.latest_data?.status_read?.definition) : undefined as any,
    isAlarm: hasLatestData ? Boolean(device?.latest_data?.alarm?.value) : false,
    alarmCode: hasLatestData ? device?.latest_data?.alarm_code?.definition : '',
    // Check online status - default to false if not available
    isOnline: hasLatestData ? (device?.latest_data?.online_status?.value === 1 || Boolean(device?.latest_data?.online_status?.value)) : false,
    // Use autopilot status if available, otherwise default to 'inactive'
    autopilotStatus: (device?.autopilot?.autopilot_status as AutoPilotStatus) || 'inactive',
    // Set default values for all numeric fields
    working: hasLatestData ? device?.latest_data?.working_percentage?.value || 0 : 0,
    avgTemp: hasLatestData ? device?.latest_data?.room_temperature?.value || null : null,
    avgRH: hasLatestData ? device?.latest_data?.relative_humidity?.value || null : null,
    pmv: hasLatestData ? device?.latest_data?.pmv?.value || null : null,
    setTemp: hasLatestData ? (device?.latest_data?.temperature_setpoint_read?.value !== undefined ? device.latest_data.temperature_setpoint_read.value : null) : null,
    returnTemp: hasLatestData ? (device?.latest_data?.return_temperature?.value !== undefined ? device.latest_data.return_temperature.value : null) : null,
    // Default mode to null if not available
    // Ensure mode values are lowercase to handle capitalized enum values from API
    mode: hasLatestData ? (device?.latest_data?.mode_read?.definition ? (device.latest_data.mode_read.definition as string).toLowerCase() as Mode : null) : null,
    // No default operation mode if not available
    opMode: hasLatestData ? (device?.latest_data?.operation_mode_read?.definition as OpMode) : undefined as any,
    // No default fan mode if not available
    fanSpeed: hasLatestData ? (device?.latest_data?.fan_mode_read?.definition as any) : undefined as any,
    power: hasLatestData ? device?.latest_data?.power?.value || 0 : 0,
    consumption: hasLatestData ? device?.latest_data?.today_energy?.value || 0 : 0,
    hours: hasLatestData ? device?.latest_data?.runtime?.value || 0 : 0,
    user: device?.autopilot?.updated_by?.username || '',
    lastUpdated: hasLatestData ? device?.latest_data?.return_temperature?.updated_at || '' : '',
  };
};

const mapZoneDataToTempData = (data: any): TempData => ({
  sensorIndoor: {
    temperature: data.avg_room_temperature?.value !== undefined ? data.avg_room_temperature.value : undefined,
    humidity: data.avg_relative_humidity?.value !== undefined ? data.avg_relative_humidity.value : undefined,
    avgToday: {
      temperature: data.today_avg_room_temperature?.value !== undefined ? data.today_avg_room_temperature.value : undefined,
      humidity: data.today_avg_relative_humidity?.value !== undefined ? data.today_avg_relative_humidity.value : undefined,
    },
  },
  ahuTemp: {
    supply: data.avg_temperature_setpoint?.value !== undefined ? data.avg_temperature_setpoint.value : undefined,
    return: data.avg_return_temperature?.value !== undefined ? data.avg_return_temperature.value : undefined,
    avgToday: {
      supply: data.today_avg_temperature_setpoint?.value !== undefined ? data.today_avg_temperature_setpoint.value : undefined,
      return: data.today_avg_return_temperature?.value !== undefined ? data.today_avg_return_temperature.value : undefined,
    },
  },
});

const updateTempData = (prevData: TempData | undefined, datapoint: string, value: number): TempData => {
  if (!prevData) {
    return {
      sensorIndoor: { temperature: undefined, humidity: undefined, avgToday: { temperature: undefined, humidity: undefined } },
      ahuTemp: { supply: undefined, return: undefined, avgToday: { supply: undefined, return: undefined } }
    };
  }

  const newData = { ...prevData };

  switch (datapoint) {
    case 'avg_room_temperature':
      newData.sensorIndoor.temperature = value;
      break;
    case 'avg_relative_humidity':
      newData.sensorIndoor.humidity = value;
      break;
    case 'today_avg_room_temperature':
      newData.sensorIndoor.avgToday.temperature = value;
      break;
    case 'today_avg_relative_humidity':
      newData.sensorIndoor.avgToday.humidity = value;
      break;
    case 'avg_temperature_setpoint':
      newData.ahuTemp.supply = value;
      break;
    case 'avg_return_temperature':
      newData.ahuTemp.return = value;
      break;
    case 'today_avg_temperature_setpoint':
      newData.ahuTemp.avgToday.supply = value;
      break;
    case 'today_avg_return_temperature':
      newData.ahuTemp.avgToday.return = value;
      break;
  }

  return newData;
};

const mappingOperationalData = (tableData: AHUData[], systemData?: DevicesResponseData, prev?: OperationalData): OperationalData => ({
  averageWorking: prev?.averageWorking || systemData?.latest_data?.avg_working_percentage?.value,
  avgToday: prev?.avgToday || systemData?.latest_data?.today_avg_working_percentage?.value,
  units: [
    {
      status: 'on',
      count: tableData?.filter(item => item.status === 'on').length || 0,
    },
    {
      status: 'off',
      // Only count devices as 'off' if they are online but turned off
      // This excludes devices with no connection/no data
      count: tableData?.filter(item => item.status === 'off' && item.isOnline).length || 0,
    },
    {
      status: 'alarm',
      count: tableData?.filter(item => item.isAlarm).length || 0,
    },
    {
      type: 'cool',
      count: tableData?.filter(item => item.status === 'on' && item.mode === 'cool').length || 0,
    },
    {
      type: 'fan',
      count: tableData?.filter(item => item.status === 'on' && item.mode === 'fan').length || 0,
    },
    {
      type: 'dry',
      count: tableData?.filter(item => item.status === 'on' && item.mode === 'dry').length || 0,
    }
  ]
});

export function AHUPage() {
  const [tableData, setTableData] = useState<AHUData[]>([]);
  const [tempData, setTempData] = useState<TempData>();
  const [operationalData, setOperationalData] = useState<OperationalData>();
  const [consumptionData, setConsumptionData] = useState({
    powerUsage: 0,
    avgToday: 0,
    consumption: 0,
  });
  const [autopilotData, setAutopilotData] = useState<AutopilotData>();
  const navigate = useNavigate();
  const { enumMappings, updateEnumMappings: updateEnumMappingsContext } = useEnumMappings();

  const zone = useContext(ZoneContext);
  const { data: systemData, isLoading, refetch: systemRefetch } = useDevices({ expand: 'latest_data,devices' });
  const { data: zoneData, isLoading: zoneLoading, refetch: zoneRefetch } = useZones({ expand: 'latest_data' }, zone?.id || '');
  const { data: autopilotsData, isLoading: autopilotLoading, refetch: autoPilotRefetch } = useAutopilotHook({ level: 'building', expand: 'total_energy' });

  // Store systemData in a ref to access latest value in subscription callbacks
  const systemDataRef = useRef<DevicesResponseData | undefined>();

  // Use callback to avoid recreating this function on every render
  const memoizedUpdateEnumMappings = useCallback((device: Device) => {
    updateEnumMappingsContext(device);
  }, [updateEnumMappingsContext]);

  useEffect(() => {
    setOperationalData((prev) => mappingOperationalData(tableData, systemData, prev));
  }, [tableData, systemData]);

  useEffect(() => {
    // Create a unique channel name to avoid conflicts
    const subscription = supabase.channel('ahu_latest_data_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'latest_data',
          // No filter to ensure we get all updates
        },
        (payload) => {
          if (!payload.new) return;

          // Type assertion to handle payload.new properties
          const { device_id, datapoint, value, definition, enum: enumValues } = payload.new as any;
          if (!device_id || !datapoint) return;

          // If we have a new enum mapping, update our context
          if (enumValues && Object.keys(enumValues).length > 0) {
            // Use the ref to access the latest systemData value
            const device = systemDataRef.current?.devices.find(d => d.device_id === device_id);
            if (device) {
              memoizedUpdateEnumMappings(device);
            }
          }

          if (device_id === 'ac_subsystem' && ['avg_working_percentage', 'today_avg_working_percentage'].includes(datapoint)) {
            setOperationalData(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                [datapoint === 'avg_working_percentage' ? 'averageWorking' : 'avgToday']: value,
              };
            });
          } else if ([
            'avg_room_temperature',
            'avg_relative_humidity',
            'today_avg_relative_humidity',
            'today_avg_relative_humidity',
            'avg_temperature_setpoint',
            'avg_return_temperature',
            'today_avg_temperature_setpoint',
            'today_avg_return_temperature'
          ].includes(datapoint)) {
            setTempData(prev => updateTempData(prev, datapoint, value));
          } else if (device_id === 'alto-cero-dev' && ['total_energy_saving', 'total_energy_consumption'].includes(datapoint)) {
            setAutopilotData(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                [datapoint === 'total_energy_saving' ? 'savedPower' : 'consumption']: value,
              };
            });
          } else if (device_id === 'ac_subsystem' && ['power', 'today_avg_power', 'today_energy'].includes(datapoint)) {
            setConsumptionData(prev => {
              if (!prev) return prev;
              const key = datapoint === 'power' ? 'powerUsage' :
                datapoint === 'today_avg_power' ? 'avgToday' : 'consumption';
              return {
                ...prev,
                [key]: value,
              };
            });
          } else {
            setTableData(prevState =>
              prevState.map(item => {
                if (item.id !== device_id) return item;

                // Map datapoint to the correct property in table data
                let propKey = datapoint;
                switch (datapoint) {
                  case 'status_read': propKey = 'status'; break;
                  case 'mode_read': propKey = 'mode'; break;
                  case 'operation_mode_read': propKey = 'opMode'; break;
                  case 'fan_mode_read': propKey = 'fanSpeed'; break;
                  case 'working_percentage': propKey = 'working'; break;
                  case 'room_temperature': propKey = 'avgTemp'; break;
                  case 'relative_humidity': propKey = 'avgRH'; break;
                  case 'temperature_setpoint_read': propKey = 'setTemp'; break;
                  case 'return_temperature': propKey = 'returnTemp'; break;
                  case 'power': propKey = 'power'; break;
                  case 'today_energy': propKey = 'consumption'; break;
                  case 'runtime': propKey = 'hours'; break;
                  case 'alarm': propKey = 'isAlarm'; break;
                  case 'alarm_code': propKey = 'alarmCode'; break;
                  case 'online_status': propKey = 'isOnline'; break;
                }

                // Process the value based on the datapoint type
                let processedValue;
                if (datapoint === 'alarm') {
                  processedValue = Boolean(value);
                } else if (datapoint === 'status_read') {
                  if (definition) {
                    // If definition is available, use it (handle capitalization)
                    processedValue = normalizeStatus(definition);
                  } else if (value !== undefined && value !== null) {
                    // If no definition but we have a value, use the enum mappings
                    const statusValue = String(value); // Convert to string for object key lookup
                    const mappedStatus = enumMappings.status[statusValue];
                    if (mappedStatus) {
                      processedValue = normalizeStatus(mappedStatus);
                    } else {
                      // Direct mapping for common values if enum mapping is not available
                      processedValue = value === 1 || value === true || value === '1' ? 'on' : 'off';
                    }
                  }
                } else if (datapoint === 'mode_read') {
                  if (definition) {
                    // If definition is available, use it (lowercase)
                    processedValue = definition.toLowerCase();
                    console.log(`Mode update for device ${device_id} using definition: ${definition} -> ${processedValue}`);
                  } else if (value !== undefined && value !== null) {
                    // If no definition but we have a value, use the enum mappings
                    const modeValue = String(value); // Convert to string for object key lookup
                    const mappedMode = enumMappings.mode[modeValue];

                    if (mappedMode) {
                      // If we have a mapping, use it (lowercase)
                      processedValue = mappedMode.toLowerCase();
                      console.log(`Mode update for device ${device_id} using enum mapping: ${value} -> ${mappedMode} -> ${processedValue}`);
                    } else {
                      // If no mapping found, log this and preserve the existing value
                      console.log(`No mode mapping found for value ${value} on device ${device_id}, preserving existing value: ${item.mode}`);
                      // Keep the existing value instead of defaulting to 'cool'
                      // If the existing value is null or undefined, we'll let it remain undefined
                      // This will cause the UI to show the value as empty rather than 'cool'
                      processedValue = item.mode !== undefined ? item.mode : undefined;
                    }
                  }
                } else if (datapoint === 'operation_mode_read') {
                  if (definition) {
                    processedValue = definition;
                  } else if (value !== undefined && value !== null) {
                    // If no definition but we have a value, use the enum mappings
                    const opModeValue = String(value); // Convert to string for object key lookup
                    const mappedOpMode = enumMappings.operationMode[opModeValue];
                    // If we have a mapping, use it, otherwise fall back to apiMapper
                    processedValue = mappedOpMode || apiMapper.operation_mode_read[value as keyof typeof apiMapper.operation_mode_read];
                  }
                } else if (datapoint === 'online_status') {
                  // For online_status, convert the numeric value to boolean
                  processedValue = value === 1 || Boolean(value);
                  console.log(`Online status update for device ${device_id}: ${processedValue}`);
                } else if (datapoint === 'fan_mode_read') {
                  if (definition) {
                    // Standardize 'Medium' to 'Med' for consistency
                    if (definition === 'Medium') {
                      processedValue = 'Med';
                    } else if (definition === 'Ultra Low') {
                      processedValue = 'Ultra Low';
                    } else if (definition === 'Fan Stop') {
                      processedValue = 'Fan Stop';
                    } else {
                      processedValue = definition;
                    }
                    console.log(`Fan mode update for device ${device_id} using definition: ${definition} -> ${processedValue}`);
                  } else if (value !== undefined && value !== null) {
                    // If no definition but we have a value, use the enum mappings
                    const fanSpeedValue = String(value); // Convert to string for object key lookup
                    const mappedValue = enumMappings.fanSpeed[fanSpeedValue];

                    if (mappedValue) {
                      // Standardize 'Medium' to 'Med' for consistency
                      if (mappedValue === 'Medium') {
                        processedValue = 'Med';
                      } else if (mappedValue === 'Ultra Low') {
                        processedValue = 'Ultra Low';
                      } else if (mappedValue === 'Fan Stop') {
                        processedValue = 'Fan Stop';
                      } else {
                        processedValue = mappedValue;
                      }
                      console.log(`Fan mode update for device ${device_id} using enum mapping: ${value} -> ${mappedValue} -> ${processedValue}`);
                    } else {
                      // If no mapping found, log this and preserve the existing value
                      console.log(`No fan mode mapping found for value ${value} on device ${device_id}, preserving existing value: ${item.fanSpeed}`);
                      // Keep the existing value instead of defaulting to 'Auto'
                      // If the existing value is null or undefined, we'll let it remain undefined
                      // This will cause the UI to show the value as empty rather than 'Auto'
                      processedValue = item.fanSpeed !== undefined ? item.fanSpeed : undefined;
                    }
                  }
                } else if (datapoint === 'temperature_setpoint_read' || datapoint === 'return_temperature' ||
                  datapoint === 'room_temperature' || datapoint === 'relative_humidity') {
                  // For numeric values, just use the value directly
                  processedValue = value;
                } else {
                  // For other fields, try definition first, then fall back to value
                  processedValue = definition || value;
                }

                return {
                  ...item,
                  [propKey]: processedValue,
                  lastUpdated: datapoint === 'return_temperature' ? payload?.commit_timestamp : item.lastUpdated,
                };
              })
            );
          }
        }
      )
      .subscribe();

    const autoPilotSubscription = supabase.channel('ahu_autopilot_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'autopilot',
        },
        async (payload) => {
          if (!payload.new) return;

          // Type assertion to handle payload.new properties
          const { device_id, autopilot_status, updated_at, updated_by_id } = payload.new as any;

          // Get username from auth_user table using updated_by_id
          let username = undefined;
          if (updated_by_id) {
            const { data: userData, error } = await supabase
              .from('auth_user')
              .select('username')
              .eq('id', updated_by_id)
              .single();

            if (!error && userData) {
              username = userData.username;
            }
          }

          if (device_id === 'alto-cero-dev') {
            setAutopilotData(prev => ({
              ...prev,
              status: autopilot_status,
              lastUpdated: updated_at,
              user: username,
            }))
          } else {
            setTableData(prevState =>
              prevState.map(item => {
                if (item.id !== device_id) return item;
                return {
                  ...item,
                  autopilotStatus: autopilot_status,
                  lastUpdated: updated_at,
                  user: username,
                };
              })
            );
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
      autoPilotSubscription.unsubscribe()
    };
  }, [memoizedUpdateEnumMappings]);

  // Update enum mappings when system data is loaded - always update to ensure we have the latest mappings
  useEffect(() => {
    if (systemData?.devices && systemData.devices.length > 0) {
      console.log('Initializing enum mappings from system data');
      // Update mappings from all devices to ensure we have complete mappings
      systemData.devices.forEach(device => {
        memoizedUpdateEnumMappings(device);
      });

      // Log the current enum mappings for debugging
      console.log('Current enum mappings after initialization:');
      console.log('Mode mappings:', enumMappings.mode);
      console.log('Fan speed mappings:', enumMappings.fanSpeed);
      console.log('Status mappings:', enumMappings.status);
      console.log('Operation mode mappings:', enumMappings.operationMode);
    }
  }, [systemData?.devices, memoizedUpdateEnumMappings]);

  // Update systemData ref for use in callbacks
  useEffect(() => {
    systemDataRef.current = systemData;
  }, [systemData]);

  useEffect(() => {
    if (!autopilotsData?.[0]) return;

    const autopilot = autopilotsData[0];
    // Type assertion to handle autopilot data
    setAutopilotData(prev => ({
      ...prev,
      id: String(autopilot.id),
      user: (autopilot as any).updated_by?.username,
      savedPower: (autopilot as any).total_energy?.total_energy_saving?.value,
      consumption: (autopilot as any).total_energy?.total_energy_consumption?.value,
      status: autopilot.autopilot_status,
      lastUpdated: autopilot.updated_at,
      currentGear: autopilot.current_gear,
    }));
  }, [autopilotsData]);

  useEffect(() => {
    // Type assertion to handle zoneData
    if (!(zoneData as any)?.latest_data) return;
    setTempData(mapZoneDataToTempData((zoneData as any).latest_data));
  }, [(zoneData as any)?.latest_data]);

  useEffect(() => {
    if (!systemData?.devices) return;

    const nextTableData = systemData.devices.map(mapDeviceToTableData);

    setTableData(nextTableData);
    setConsumptionData({
      powerUsage: systemData?.latest_data?.power?.value || 0,
      avgToday: systemData?.latest_data?.today_avg_power?.value || 0,
      consumption: systemData?.latest_data?.today_energy?.value || 0,
    });
  }, [systemData]);

  useEffect(() => {
    systemRefetch();
    autoPilotRefetch();
    zoneRefetch();
  }, [navigate, systemRefetch, autoPilotRefetch, zoneRefetch]);

  useEffect(() => {
    zoneRefetch()
  }, [zone?.id])

  // Set up polling as a fallback for real-time updates
  useEffect(() => {
    // Poll every 5 minutes (300000 ms) as a fallback
    const pollingInterval = setInterval(() => {
      console.log('Fallback polling: refreshing data');
      systemRefetch();
      autoPilotRefetch();
      zoneRefetch();
    }, 300000);

    return () => clearInterval(pollingInterval);
  }, [systemRefetch, autoPilotRefetch, zoneRefetch]);

  // Create a refreshAll function to handle refreshing all data
  const refreshAllData = () => {
    console.log('Manual refresh: refreshing data');
    systemRefetch();
    autoPilotRefetch();
    zoneRefetch();
  };

  // Initialize operationalData if it's undefined
  useEffect(() => {
    if (!operationalData && tableData.length > 0) {
      setOperationalData(mappingOperationalData(tableData, systemData));
    }
  }, [operationalData, tableData, systemData]);

  if (isLoading || zoneLoading || autopilotLoading || !systemData || !zoneData || !autopilotData) {
    return (
      <div className="space-y-6 p-6">
        {/* Header skeleton */}
        <div className="flex justify-between items-center">
          <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-8 w-32">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
          </div>
          <div className="flex gap-2">
            <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-8 w-24">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
            </div>
            <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-8 w-20">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
            </div>
          </div>
        </div>
        
        {/* Table skeleton */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-8 gap-4 p-4 bg-gray-50 border-b">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="relative overflow-hidden rounded-md bg-gray-200/40 h-4">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
              </div>
            ))}
          </div>
          
          {/* Table rows */}
          {Array.from({ length: 10 }).map((_, rowIndex) => (
            <div key={rowIndex} className="grid grid-cols-8 gap-4 p-4 border-b last:border-b-0">
              {Array.from({ length: 8 }).map((_, colIndex) => (
                <div key={colIndex} className="relative overflow-hidden rounded-md bg-gray-200/40 h-4">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Ensure operationalData is defined before rendering
  if (!operationalData) {
    return (
      <div className="space-y-6 p-6">
        {/* Header skeleton */}
        <div className="flex justify-between items-center">
          <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-8 w-32">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
          </div>
          <div className="flex gap-2">
            <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-8 w-24">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
            </div>
            <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-8 w-20">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
            </div>
          </div>
        </div>
        
        {/* Table skeleton */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-8 gap-4 p-4 bg-gray-50 border-b">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="relative overflow-hidden rounded-md bg-gray-200/40 h-4">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
              </div>
            ))}
          </div>
          
          {/* Table rows */}
          {Array.from({ length: 10 }).map((_, rowIndex) => (
            <div key={rowIndex} className="grid grid-cols-8 gap-4 p-4 border-b last:border-b-0">
              {Array.from({ length: 8 }).map((_, colIndex) => (
                <div key={colIndex} className="relative overflow-hidden rounded-md bg-gray-200/40 h-4">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 font-inter h-full overflow-auto" style={{ fontFamily: 'Inter, sans-serif !important' }}>
      <div className="flex w-full gap-3 max-w">
        <div className="w-[250px] shrink-0">
          <OperationInsights
            averageWorking={operationalData?.averageWorking}
            avgToday={operationalData?.avgToday}
            units={operationalData?.units as any}
          />
        </div>
        <div className="w-[250px] shrink-0">
          <TemperatureCard data={tempData as any} />
        </div>
        <div className="w-[calc(100%-524px)]">
          <ConsumptionCard
            powerUsage={{ current: consumptionData.powerUsage, avgToday: consumptionData.avgToday }}
            energyConsumption={{ current: consumptionData.consumption }}
          />
        </div>
      </div>
      <div className="w-full">
        <ErrorBoundary
          onReset={refreshAllData}
          fallback={
            <div className="bg-white rounded-lg shadow-sm border border-white-blue p-8 text-center">
              <p className="text-red-500 mb-4">There was an error loading the AHU table</p>
              <Button onClick={refreshAllData} className="bg-primary text-white hover:bg-primary/90">
                Retry
              </Button>
            </div>
          }
        >
          <AHUTable
            tableData={tableData}
            isLoading={isLoading}
            onRefresh={refreshAllData}
          />
        </ErrorBoundary>
      </div>
    </div>
  );
}